/**
 * AI 回覆補收判定（change: ai-response-server-persistence；多裝置友善重構）
 *
 * 【為何重構】原版以獨立的 sessionStorage 旗標記錄「已送出、尚未收到回覆」的提問，供斷線補收。
 * 但 sessionStorage 綁定瀏覽情境（session）——手機 WebView 被 OS 銷毀後重建即是全新 session，
 * 旗標隨之消失，導致「提問已送出、答案已在後端暫存，前端卻永遠不會去補收」。而摧毀 WebView
 * 這件事，恰好就是我們要保護的失敗情境本身，形成自我矛盾。
 *
 * 【新做法】以「聊天紀錄本身」為唯一真相（chatHistory 存於 IndexedDB，不綁 session、WebView 重建後仍在）：
 * 若某對話最後一則是使用者提問（sender==='guest'）且其後沒有任何回覆，即視為有待補收的缺口，
 * 由 useChat 以該提問的 id（= 發送時帶入 /send 的 userMessageId）呼叫 GET /web/chat/{chatId}/reply 補收。
 * 補收落地時採用後端回傳的 assistantMessageId，與正常路徑收斂到同一 id（append 依 id 天然去重）。
 *
 * 【墓碑集合】另維護一份 durable 的「已放棄」集合（localStorage）：僅供「使用者主動停止」使用，
 * 代表使用者明確表示不要這個答案了，永久略過。
 *
 * 【冷卻集合，非永久放棄】not_found／輪詢逾時 ≠ 使用者主動放棄——這兩種情況都無法排除
 * 「後端其實還沒收到、或還在處理」的可能（例如 /send 這個 request 在手機訊號差時還在路上，
 * WebView 就已經被砍掉並重建；或 GenAIus 回覆較久剛好卡在單次輪詢上限）。若把它們當成永久放棄，
 * 一旦後端稍後真的把答案寫進暫存表，也不會再有人去查，答案就此遺失。
 * 因此改為「冷卻後重試」：短暫暫緩掃描（漸增退避），但仍在 RECOVER_WINDOW_MS 內持續給機會，
 * 由使用者之後任一次重新開啟對話/回前景/WS 重連時自動再查一次。
 */

const ABANDONED_KEY = 'aiAbandonedReplies';
// 墓碑保留時間：對齊後端暫存表 TTL（7 天），過期即清理，避免集合無限長大
const ABANDONED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// 可補收時間窗：只對「近期」未回覆的提問補收；後端暫存表 7 天後即清除，
// 超過此窗的孤兒提問一定撈不到，不需反覆對它打 not_found
const RECOVER_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const RETRY_LATER_KEY = 'aiRetryLaterReplies';
// not_found／輪詢逾時後的重試冷卻秒數（change: unify-reply-completion-signal Section 5 後再次調整）：
// 固定 30 秒，不再遞增到 60/90 秒。原本遞增設計是在「終局時限」機制存在之前，靠冷卻長度自己
// 節制無限重試的唯一防線；現在已有 180 秒終局時限兜底（見 useChat.js failNotFound），冷卻的角色
// 單純是「這 180 秒內能查幾次」，沒有理由再保守地拖到分鐘級——冷卻越短，在終局時限判死之前
// 「剛好撞上答案已經寫進暫存表」的機會就越多，能降低「其實已經回覆了，卻被判定送達失敗」的
// 假陽性機率。30 秒對齊心跳觸發點（每 15 秒一次）的兩倍，仍有節流效果，不會變成每次心跳都真的打
// server。
const RETRY_COOLDOWN_MS = [30 * 1000];

// ── 墓碑集合（durable，localStorage）──────────────────────────────────────

/**
 * 讀取墓碑集合（id → 標記時間戳的物件）。
 * @returns {Object<string, number>}
 */
const readAbandoned = () => {
  try {
    const raw = localStorage.getItem(ABANDONED_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch (e) {
    return {};
  }
};

/**
 * 寫回墓碑集合。
 * @param {Object<string, number>} obj
 */
const writeAbandoned = (obj) => {
  try {
    localStorage.setItem(ABANDONED_KEY, JSON.stringify(obj));
  } catch (e) {
    // localStorage 不可用（隱私模式等）：退化為「無墓碑」，最壞情況只是多打一次 not_found，不影響正確性
  }
};

/**
 * 就地移除過期墓碑。
 * @param {Object<string, number>} obj
 * @param {number} nowMs
 * @returns {boolean} 是否有變更（供呼叫方決定要不要寫回）
 */
const pruneAbandoned = (obj, nowMs) => {
  let changed = false;
  for (const [id, ts] of Object.entries(obj)) {
    if (typeof ts !== 'number' || nowMs - ts > ABANDONED_TTL_MS) {
      delete obj[id];
      changed = true;
    }
  }
  return changed;
};

/**
 * 標記某提問為「已放棄」（使用者主動停止、或終局失敗時呼叫）——同步、durable。
 * 之後 findRecoverableReply 掃描到此 id 一律略過。
 * @param {string} userMessageId 提問訊息 id
 */
export const markAbandoned = (userMessageId) => {
  if (!userMessageId) return;
  const now = Date.now();
  const obj = readAbandoned();
  pruneAbandoned(obj, now);
  obj[userMessageId] = now;
  writeAbandoned(obj);
};

/**
 * 查詢某提問是否已被標記放棄（順帶清除過期墓碑）。
 * @param {string} userMessageId 提問訊息 id
 * @returns {boolean}
 */
export const isAbandoned = (userMessageId) => {
  if (!userMessageId) return false;
  const now = Date.now();
  const obj = readAbandoned();
  if (pruneAbandoned(obj, now)) writeAbandoned(obj);
  return Object.prototype.hasOwnProperty.call(obj, userMessageId);
};

// ── 冷卻集合（durable，localStorage；not_found／輪詢逾時用，非永久放棄）──────────

/**
 * 讀取冷卻集合（id → { nextRetryAt, attempts }）。
 * @returns {Object<string, {nextRetryAt: number, attempts: number}>}
 */
const readRetryLater = () => {
  try {
    const raw = localStorage.getItem(RETRY_LATER_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch (e) {
    return {};
  }
};

/**
 * 寫回冷卻集合。
 * @param {Object<string, {nextRetryAt: number, attempts: number}>} obj
 */
const writeRetryLater = (obj) => {
  try {
    localStorage.setItem(RETRY_LATER_KEY, JSON.stringify(obj));
  } catch (e) {
    // localStorage 不可用：退化為「無冷卻」，最壞情況只是重試更頻繁，不影響正確性
  }
};

/**
 * 就地移除已經過了冷卻時間很久、視為過期的條目（此處直接沿用墓碑的 TTL 概念，
 * 避免集合無限長大；實際能否再補收仍由 findRecoverableReply 的時間窗與缺口偵測決定）。
 * @param {Object<string, {nextRetryAt: number, attempts: number}>} obj
 * @param {number} nowMs
 * @returns {boolean} 是否有變更
 */
const pruneRetryLater = (obj, nowMs) => {
  let changed = false;
  for (const [id, entry] of Object.entries(obj)) {
    const nextRetryAt = entry && typeof entry.nextRetryAt === 'number' ? entry.nextRetryAt : NaN;
    if (Number.isNaN(nextRetryAt) || nowMs - nextRetryAt > ABANDONED_TTL_MS) {
      delete obj[id];
      changed = true;
    }
  }
  return changed;
};

/**
 * 標記某提問「本次查無結果，稍後才可再查」——not_found 或輪詢逾時時呼叫，取代永久放棄。
 * 依「這個提問已經冷卻過幾次」計算下一次退避時長（漸增），而非每次固定冷卻。
 * @param {string} userMessageId 提問訊息 id
 */
export const markRetryLater = (userMessageId) => {
  if (!userMessageId) return;
  const now = Date.now();
  const obj = readRetryLater();
  pruneRetryLater(obj, now);
  const prevAttempts = obj[userMessageId]?.attempts;
  const attempts = typeof prevAttempts === 'number' ? prevAttempts + 1 : 0;
  const cooldown = RETRY_COOLDOWN_MS[Math.min(attempts, RETRY_COOLDOWN_MS.length - 1)];
  obj[userMessageId] = { nextRetryAt: now + cooldown, attempts };
  writeRetryLater(obj);
};

/**
 * 查詢某提問是否仍在冷卻中（尚不到下次可重試的時間）。
 * @param {string} userMessageId 提問訊息 id
 * @returns {boolean}
 */
export const isRetryingTooSoon = (userMessageId) => {
  if (!userMessageId) return false;
  const now = Date.now();
  const obj = readRetryLater();
  if (pruneRetryLater(obj, now)) writeRetryLater(obj);
  const entry = obj[userMessageId];
  return !!entry && now < entry.nextRetryAt;
};

/**
 * 清除某提問的冷卻紀錄——補收成功落地後呼叫，避免集合殘留無用條目。
 * 並非必要（落地後 findRecoverableReply 本來就掃不到已有回覆的提問），僅為保持集合乾淨。
 * @param {string} userMessageId 提問訊息 id
 */
export const clearRetryLater = (userMessageId) => {
  if (!userMessageId) return;
  const obj = readRetryLater();
  if (Object.prototype.hasOwnProperty.call(obj, userMessageId)) {
    delete obj[userMessageId];
    writeRetryLater(obj);
  }
};

// ── 終局送達失敗集合（durable，localStorage；change: unify-reply-completion-signal Section 5）
// ──────────────────────────────────────────────────────────────────────────
// 與墓碑集合（markAbandoned）語意不同：兩者都會讓 findRecoverableReply 永久排除該 id（不再無聲
// 重試），但 markAbandoned 代表「使用者主動停止」，本集合代表「系統判定終局時限已到、判定送達
// 失敗」——UI 需要分辨兩者：前者已顯示過停止的系統通知，後者要顯示「可能未送達＋重新發送」的
// 失敗提示（見 useChat.js 終局時限判斷、ChatMessage.js 的重送按鈕），故另立一份集合。

const DELIVERY_FAILED_KEY = 'aiDeliveryFailedMessages';

const readDeliveryFailed = () => {
  try {
    const raw = localStorage.getItem(DELIVERY_FAILED_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch (e) {
    return {};
  }
};

const writeDeliveryFailed = (obj) => {
  try {
    localStorage.setItem(DELIVERY_FAILED_KEY, JSON.stringify(obj));
  } catch (e) {
    // localStorage 不可用：退化為「這次重整後看不到失敗提示」，不影響 markAbandoned 已經
    // 排除重試這件事本身（兩者各自獨立寫入，其中一個寫入失敗不影響另一個）
  }
};

/**
 * 標記某提問為「終局送達失敗」——僅供 useChat.js 的終局時限判斷呼叫，使用者停止（stopGeneration）
 * 不應呼叫這個，那種情境已有專屬的系統通知，不需要「重新發送」按鈕。
 * @param {string} userMessageId 提問訊息 id
 */
export const markDeliveryFailed = (userMessageId) => {
  if (!userMessageId) return;
  const now = Date.now();
  const obj = readDeliveryFailed();
  pruneAbandoned(obj, now); // 沿用相同 TTL 概念，避免集合無限長大
  obj[userMessageId] = now;
  writeDeliveryFailed(obj);
};

/**
 * 查詢某提問是否被標記為終局送達失敗，供 ChatMessage.js 決定是否顯示失敗提示與重送按鈕。
 * @param {string} userMessageId 提問訊息 id
 * @returns {boolean}
 */
export const isDeliveryFailed = (userMessageId) => {
  if (!userMessageId) return false;
  const now = Date.now();
  const obj = readDeliveryFailed();
  if (pruneAbandoned(obj, now)) writeDeliveryFailed(obj);
  return Object.prototype.hasOwnProperty.call(obj, userMessageId);
};

/**
 * 清除「終局送達失敗」標記——僅供使用者按下重新發送、補查發現答案其實已經存在時呼叫。
 * 這是使用者主動觸發、當下就看得到結果的動作，不是背景偷偷把失敗翻盤成成功，
 * 不違反「顯示失敗後不應無聲又變回成功」的原則（見 useChat.js resendMessage 說明）。
 * @param {string} userMessageId 提問訊息 id
 */
export const clearDeliveryFailed = (userMessageId) => {
  if (!userMessageId) return;
  const obj = readDeliveryFailed();
  if (Object.prototype.hasOwnProperty.call(obj, userMessageId)) {
    delete obj[userMessageId];
    writeDeliveryFailed(obj);
  }
};

// ── 缺口偵測 ──────────────────────────────────────────────────────────────

/**
 * 從一段訊息陣列判斷是否存在「待補收的 AI 回覆缺口」。
 *
 * 判定條件（全部成立才算缺口）：
 *   1. 最後一則訊息為使用者提問（sender==='guest'）——其後若已接任何回覆/系統訊息即無缺口
 *   2. 該提問非真人客服提問（!isHuman）——真人客服回覆走 WebSocket，不經 AI 回覆暫存表
 *   3. 提問有 id（即發送時帶入 /send 的 userMessageId）
 *   4. 提問未被標記放棄（使用者未主動停止；not_found／逾時不算放棄，見條件 4b）
 *   4b. 提問未在冷卻中（not_found／輪詢逾時後的漸增退避尚未過期）
 *   5. 提問在可補收時間窗內（避免對古老孤兒提問反覆輪詢）
 *
 * @param {Array} messages 目前對話的訊息陣列（依時間排序，最後一筆為最新）
 * @param {string} chatId 對話 id
 * @param {{ignoreCooldown?: boolean}} [opts] ignoreCooldown 為 true 時跳過條件 4b（冷卻檢查）。
 *   僅供「使用者主動切回前景」這種明確、低頻的觸發點使用（見 useChat.js 觸發點2），
 *   讓切回來一定會真的查一次，不被 not_found／逾時留下的漸增退避冷卻卡住；
 *   心跳／載入掃描／WS 重連等自動觸發點不可傳 true，否則會失去冷卻原本要防的洗版效果。
 * @returns {{chatId: string, userMessageId: string}|null} 有缺口時回傳補收所需 id，否則 null
 */
/**
 * 條件 1/2/3/4/5 的共用判定（不含 4b 冷卻），回傳最後一則提問或 null。
 * findRecoverableReply／hasPendingGap 共用，避免兩處各寫一份條件判斷。
 * @param {Array} messages
 * @param {string} chatId
 * @returns {Object|null} 符合前述條件的最後一則提問訊息，否則 null
 */
const lastUnresolvedQuestion = (messages, chatId) => {
  if (!chatId || !Array.isArray(messages) || messages.length === 0) return null;
  const last = messages[messages.length - 1];
  if (!last || last.sender !== 'guest') return null; // 條件 1
  if (last.isHuman) return null; // 條件 2
  if (!last.id) return null; // 條件 3
  if (isAbandoned(last.id)) return null; // 條件 4
  // 條件 5：時間窗
  const createdMs = last.createdAt ? Date.parse(last.createdAt) : NaN;
  if (!Number.isNaN(createdMs) && Date.now() - createdMs > RECOVER_WINDOW_MS) return null;
  return last;
};

export const findRecoverableReply = (messages, chatId, { ignoreCooldown = false } = {}) => {
  const last = lastUnresolvedQuestion(messages, chatId);
  if (!last) return null;
  if (!ignoreCooldown && isRetryingTooSoon(last.id)) return null; // 條件 4b：是否要「現在真的去問 server」才看冷卻
  // createdAt 供 useChat.js 的終局時限判斷使用（change: unify-reply-completion-signal Section 5）：
  // 判斷的是「這則提問從送出到現在總共過了多久」，而非單次輪詢的耗時，故需要提問本身的時間戳記。
  return { chatId, userMessageId: last.id, createdAt: last.createdAt };
};

/**
 * 純粹用於畫面顯示的「是否有未解決的提問」判定：條件跟 findRecoverableReply 一樣，
 * 但刻意**不**檢查冷卻期（4b）——冷卻期只影響「要不要現在真的去打 /reply」，不代表答案已經
 * 確定不存在，畫面上的「處理中」等待態應該在冷卻期間持續顯示，不隨冷卻週期一閃一閃。
 * @param {Array} messages
 * @param {string} chatId
 * @returns {boolean}
 */
export const hasPendingGap = (messages, chatId) => !!lastUnresolvedQuestion(messages, chatId);
